import { Injectable } from '@angular/core';
import { ISplugDefinition, SplugDefinitionRemoteControllerService } from 'core/api';
import { LControlType } from 'core/dynamic-form';
import { filterAllTruthyInTuple, subjectFromObs } from 'core/rx-js';
import { LLanguage, LanguageService, LoaderService } from 'core/services';
import { KeyMapUtil } from 'core/type-utils';
import { BehaviorSubject, combineLatest, filter, map, switchMap } from 'rxjs';

type SplugParamsParser = KeyMapUtil<LControlType, {
  checkbox: (value: string) => boolean,
  checklist: (value: string) => string[],
  date: (value: string) => Date,
}>

type SplugParamsStringifier = KeyMapUtil<LControlType, {
  checkbox: (value: boolean) => string,
  checklist: (value: string[]) => string,
  date: (value: Date) => string,
}>



@Injectable({
  providedIn: 'root'
})
export class SplugConfigService {

  readonly authenticatedSubject = new BehaviorSubject<boolean>(false);

  readonly selectedLanguageSubject = this._languageService.selectedLanguageSubject
  get selectedLanguage() {
    return this.selectedLanguageSubject.value;
  }

  readonly splugLanguageDefinitionSubject = new BehaviorSubject<Partial<Record<LLanguage, ISplugDefinition[]>>>({})

  readonly splugDefinitionsSubject = subjectFromObs({
    observable: combineLatest([this.splugLanguageDefinitionSubject, this.selectedLanguageSubject]).pipe(
      filterAllTruthyInTuple(),
      map(([splugLanguageDefinition, selectedLanguage]) => splugLanguageDefinition[selectedLanguage])
    ),
    initialValue: null
  })


  readonly splugParamsParser: SplugParamsParser = {
    checkbox: (value) => value === 'true',
    date: (date) => new Date(date),
    checklist: (value) => value?.split(',')
  }



  readonly splugParamsStringifiers: SplugParamsStringifier = {
    checkbox: (value) => value + '',
    date: (date) => date.toString(),
    checklist: (value) => !value ? null : value.join(',')
  }

  get splugDefinitions() {
    return this.splugDefinitionsSubject.value;
  }



  constructor(
    private readonly _splugDefinitionRemoteControllerService: SplugDefinitionRemoteControllerService,
    private readonly _languageService: LanguageService,
    private readonly _loaderService: LoaderService

  ) {


    combineLatest([this.splugLanguageDefinitionSubject, this.selectedLanguageSubject, this.authenticatedSubject]).pipe(
      filter(([splugLanguageDefinition, selectedLanguage, authenticated]) => !!selectedLanguage && authenticated && !splugLanguageDefinition[selectedLanguage]),
      //interceptor injects language
      switchMap(() => {
        const id = this._loaderService.show({ key: 'splugs.get', text: 'ss-splug.get.pending' });
        return this._splugDefinitionRemoteControllerService.getMany().pipe(
          this._loaderService.disposeLoader(id)
        )
      })
    )
      .subscribe((definitions) => {

        const splugLanguageDefinition = this.splugLanguageDefinitionSubject.value;

        this.splugLanguageDefinitionSubject.next({
          ...splugLanguageDefinition,
          [this.selectedLanguage]: definitions
        })
      })

    this.authenticatedSubject.pipe(
      filter(authenticated => !authenticated)
    )
      .subscribe(() => {
        this.splugLanguageDefinitionSubject.next({})
      })
  }


  getControlType(splugId: string, key: string) {
    const splugDefinition = this.splugDefinitions.find((value) => value.id === splugId);

    const control = splugDefinition?.controls?.find((value) => value.dataField === key);
    return control?.type;
  }

  executeParser(splugId: string, key: string, value: any) {
    const controlType = this.getControlType(splugId, key);
    if (!controlType) {
      return value;
    }
    const parse = (this.splugParamsParser as any)[controlType];

    if (!parse) {
      return value;
    }
    return parse(value)
  }

  executeStringifer(splugId: string, key: string, value: any) {
    const controlType = this.getControlType(splugId, key);
    if (!controlType) {
      return value;
    }
    const stringify = (this.splugParamsStringifiers as any)[controlType];
    if (!stringify) {
      return value;
    }

    return stringify(value)
  }

  getSplugType(splugId: string) {

    const splugType = this.splugDefinitions.find((value) => value.id === splugId).splugId;
    return splugType;
  }

}
